import EVENTS from "core/customEvent/events.js";
//import external from '../externalModules.js';
import anyHandlesOutsideImage from "./anyHandlesOutsideImage.js";
import { removeToolState } from "component/toolManager/toolState.js";
import triggerEvent from "core/customEvent/triggerEvent.js";
//import { clipToBox } from "component/util/clip.js";
import math from "core/math";
import { state } from "component/molecules/viewport/toolStates/store.js";
import { getEnabledElement } from "component/toolManager/enabledElements.js";

const _dragEvents = {
  mouse: [EVENTS.MOUSE_DRAG],
  touch: [EVENTS.TOUCH_DRAG]
};

const _upOrEndEvents = {
  mouse: [EVENTS.MOUSE_UP, EVENTS.MOUSE_CLICK],
  touch: [
    EVENTS.TOUCH_END,
    EVENTS.TOUCH_DRAG_END,
    EVENTS.TOUCH_PINCH,
    EVENTS.TOUCH_PRESS,
    EVENTS.TAP
  ]
};

/**
 * 모든 핸들 + 중간 점들의 좌표를 모두 조작
 * Manipulator to move all provided handles at the same time
 * @public
 * @function moveAllHandles
 * @memberof Manipulators
 *
 * @param {*}        evtDetail
 * @param {*}        evtDetail.element
 * @param {String}   toolName
 * @param {*}        annotation
 * @param {*}        [handle=null] - not needed by moveAllHandles, but keeps call signature the same as `moveHandle`
 * @param {Object}   [options={}]
 * @param {Boolean}  [options.deleteIfHandleOutsideImage]
 * @param {function} [options.doneMovingCallback]
 * @param {Boolean}  [options.preventHandleOutsideImage]
 * @param {string}   [interactionType=mouse]
 * @returns {undefined}
 */
export default function(
  { element },
  toolName,
  annotation,
  handle,
  options = {},
  interactionType = "mouse"
) {
  // Use global defaults, unless overidden by provided options
  options = Object.assign(
    {
      deleteIfHandleOutsideImage: state.deleteIfHandleOutsideImage,
      preventHandleOutsideImage: state.preventHandleOutsideImage
    },
    options
  );

  const dragHandler = _dragHandler.bind(this, toolName, annotation, options);

  const upOrEndHandler = evt => {
    _upOrEndHandler(
      toolName,
      annotation,
      options,
      interactionType,
      {
        dragHandler,
        upOrEndHandler
      },
      evt
    );
  };

  annotation.active = true;
  state.isToolLocked = true;

  // Add Event Listeners
  _dragEvents[interactionType].forEach(eventType => {
    //element.addEventListener(eventType, dragHandler);
    element.element.addEventListener(eventType, dragHandler);
  });
  _upOrEndEvents[interactionType].forEach(eventType => {
    //element.addEventListener(eventType, upOrEndHandler);
    element.element.addEventListener(eventType, upOrEndHandler);
  });
}

function _dragHandler(toolName, annotation, options = {}, evt) {
  const { element, image } = evt.detail;
  const { x, y } = evt.detail.deltaPoints.image;

  annotation.active = true;
  annotation.invalidated = true;

  const handleKeys = Object.keys(annotation.handles);

  for (let i = 0; i < handleKeys.length; i++) {
    const key = handleKeys[i];
    const handle = annotation.handles[key];

    if (
      // Don't move this part of the annotation
      handle.movesIndependently === true ||
      // Not a true handle
      !handle.hasOwnProperty("x") ||
      !handle.hasOwnProperty("y")
    ) {
      continue;
    }

    handle.x += x;
    handle.y += y;

    if (options.preventHandleOutsideImage) {
      math.range.clipToBox(handle, image);
    }
  }

  /// middle point가 저장되는 도구의 경우, middle points 의 점을 모두 이동함
  if (annotation.storeMiddlePoint && annotation.storeMiddlePoint === true) {
    annotation.middlePoint.forEach((value, index) => {
      value.x += evt.detail.deltaPoints.image.x;
      value.y += evt.detail.deltaPoints.image.y;
      if (options.preventHandleOutsideImage) {
        math.range.clipToBox(value, image);
      }
    });
  }

  let enabledElement = getEnabledElement(element.element);
  enabledElement.needsRedraw = true;

  const eventType = EVENTS.MEASUREMENT_MODIFIED;
  const modifiedEventData = {
    toolName,
    element,
    measurementData: annotation
  };

  triggerEvent(element.element, eventType, modifiedEventData);

  evt.preventDefault();
  evt.stopPropagation();
}

function _upOrEndHandler(
  toolName,
  annotation,
  options = {},
  interactionType,
  { dragHandler, upOrEndHandler },
  evt
) {
  const eventData = evt.detail;
  const element = evt.detail.element;

  annotation.active = false;
  annotation.invalidated = true;
  state.isToolLocked = false;

  ///
  /// drag 및 mouseup 이벤트 핸들러를 모두 제거
  ///
  _dragEvents[interactionType].forEach(eventType => {
    element.element.removeEventListener(eventType, dragHandler);
  });
  _upOrEndEvents[interactionType].forEach(eventType => {
    element.element.removeEventListener(eventType, upOrEndHandler);
  });

  ///
  /// 핸들이 이미지 밖에 있을경우, 옵션에 따라 해당 그리기 데이터를 삭제
  ///
  if (
    options.deleteIfHandleOutsideImage &&
    anyHandlesOutsideImage(eventData, annotation.handles)
  ) {
    removeToolState(element.element, toolName, annotation);
  }

  if (typeof options.doneMovingCallback === "function") {
    options.doneMovingCallback();
  }

  let enabledElement = getEnabledElement(element.element);
  enabledElement.needsRedraw = true;
}
